import { useMemo } from "react";
import uniq from "lodash/uniq";
import { UIBrandedFare, UIFlightData } from "@flights/types";
import { isFeatureRunningClientSide, localizationUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "../../../hooks/useRouteName";
import useEvents from "../../../hooks/useEvents";

export type UnifiedPriceBreakdownFarePriceControllerProps = {
  fareOffer?: UIBrandedFare;
  baseOffer?: UIFlightData;
};

export function useUnifiedPriceBreakdownFarePriceController(props: UnifiedPriceBreakdownFarePriceControllerProps) {
  const { baseOffer, fareOffer } = props;
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const segments = useMemo(() => baseOffer?.segments, [baseOffer]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );
  const unifiedPriceBreakdown = useMemo(() => {
    const upb = fareOffer?.unifiedPriceBreakdown || baseOffer?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownFarePriceController - product id is missing - route "${routeName}"`
      );
    }

    return upb;
  }, [baseOffer, fareOffer, routeName, squeak]);

  const disclaimers = useMemo(() => {
    return uniq(transformDisclaimersForPriceBreakdown(fareOffer?.priceBreakdown || baseOffer?.priceBreakdown));
  }, [fareOffer, baseOffer]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    disclaimers,
    expandedByDefault
  };
}
